import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, switchMap, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiEndpointValue } from '../constants/api-endpoints';
import { TokenStorageService } from './token-storage.service';

export interface RequestOptions {
  headers?: HttpHeaders | { [header: string]: string | string[] };
  params?:
    | HttpParams
    | {
        [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
      };
  reportProgress?: boolean;
  responseType?: any;
  withCredentials?: boolean;
  observe?: any;
  isAuthNeeded?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly baseUrl = environment.apiBaseUrl.replace(/\/+$/, '');

  constructor(private readonly http: HttpClient, private readonly tokens: TokenStorageService) {}

  post(endpoint: string, body: any, options: RequestOptions = {}): Observable<any> {
    const requestOptions = this.applyHeaders(options);
    return this.http.post(this.buildUrl(endpoint), body, requestOptions).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  postHandler(apiInfo: ApiEndpointValue, payload: any = {}, options: RequestOptions = {}): Observable<any> {
    if (!apiInfo) {
      return throwError(() => new Error('Api information is missing'));
    }

    const finalPayload = this.updatePayload(apiInfo, { ...payload });
    const isAuthNeeded = options.isAuthNeeded ?? apiInfo.isAuthNeeded ?? true;

    return this.post(apiInfo.apiPath, finalPayload, { ...options, isAuthNeeded }).pipe(
      switchMap((response) => this.handleApiResponse(response))
    );
  }

  fetchApiData(payload: any, path: string): Observable<any[]> {
    return this.post(path, payload).pipe(
      map((response) => {
        if (response && response.code === '000' && Array.isArray(response.data)) {
          return response.data;
        }
        return response?.code ?? response;
      })
    );
  }

  fetchBinaryData(apiInfo: ApiEndpointValue, payload: any = {}, options: RequestOptions = {}): Observable<any> {
    if (!apiInfo) {
      return throwError(() => new Error('Api information is missing'));
    }

    const finalPayload = this.updatePayload(apiInfo, { ...payload });
    const isAuthNeeded = options.isAuthNeeded ?? apiInfo.isAuthNeeded ?? true;
    const requestOptions: any = this.applyHeaders({
      ...options,
      responseType: 'blob' as 'json',
      observe: 'response',
      isAuthNeeded
    });

    return (this.http.post(this.buildUrl(apiInfo.apiPath), finalPayload, requestOptions) as Observable<any>)
      .pipe(
        map((response: HttpResponse<Blob>) => {
          const contentType = response.headers.get('Content-Type');
          const contentDisposition = response.headers.get('Content-Disposition');
          let filename = 'download';

          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?(.+)"?/);
            if (match) {
              filename = match[1];
            }
          }

          const blob = new Blob([response.body!], { type: contentType || 'application/octet-stream' });
          return { blob, filename: filename.toLowerCase(), contentType };
        }),
        catchError((error) => this.handleError(error))
      );
  }

  private handleApiResponse(response: any): Observable<any> {
    if (!response) {
      return throwError(() => new Error('Empty response from server'));
    }

    if (`${response.code ?? ''}` === '000') {
      return of(response);
    }

    if (response.code === '999') {
      let message = response.msg || 'Unknown API error';
      const match = message.match(/Detail: Key \(["']?([\w]+)["']?\)=\((.*?)\) already exists\./);
      if (match) {
        message = `${match[2]} already exists`;
        console.warn(message);
      }
      return throwError(() => new Error(message));
    }

    const fallbackMessage = response.msg || response.message || 'Unknown API error';
    return throwError(() => new Error(fallbackMessage));
  }

  private handleError(error: any): Observable<never> {
    console.error('HTTP Error:', error);
    let errorMessage = 'An error occurred.';

    if (error?.status) {
      switch (error.status) {
        case 0:
          errorMessage = '0 - Network error or the application might be down.';
          break;
        case 400:
          errorMessage = '400 - Bad request. Please check your input.';
          break;
        case 401:
          errorMessage = '401 - Unauthorized access. Please log in.';
          break;
        case 403:
          errorMessage = '403 - Access denied.';
          break;
        case 404:
          errorMessage = '404 - Requested resource not found.';
          break;
        case 409:
          errorMessage = '409 - Conflict detected.';
          break;
        case 413:
          errorMessage = '413 - Request Entity Too Large.';
          break;
        case 500:
          errorMessage = '500 - Internal server error.';
          break;
        case 502:
          errorMessage = '502 - Bad gateway.';
          break;
        case 503:
          errorMessage = '503 - Service unavailable.';
          break;
        case 504:
          errorMessage = '504 - Gateway timeout.';
          break;
        default:
          errorMessage = `Unexpected Error: ${error.status}`;
      }
    }

    if (error?.error) {
      if (Array.isArray(error.error.errors) && error.error.errors.length > 0) {
        errorMessage = error.error.errors[0].message || errorMessage;
      } else if (error.error.message) {
        errorMessage = error.error.message;
      }
    } else if (!error?.status && error?.message) {
      errorMessage = error.message;
    }

    return throwError(() => new Error(errorMessage));
  }

  private updatePayload(apiInfo: ApiEndpointValue, payload: any): any {
    const updatedPayload = payload || {};
    updatedPayload.service = apiInfo.service;
    updatedPayload.actionType = apiInfo.actionType;

    if (apiInfo.filters && !updatedPayload.filters) {
      updatedPayload.filters = apiInfo.filters;
      updatedPayload.pagination ??= false;
      updatedPayload.pageNo ??= 0;
      updatedPayload.rowCount ??= 0;
    }

    return updatedPayload;
  }

  private applyHeaders(options: RequestOptions): RequestOptions {
    const { headers, isAuthNeeded = true, ...rest } = options;
    let resolvedHeaders = headers instanceof HttpHeaders ? headers : new HttpHeaders(headers ?? {});

    if (!resolvedHeaders.has('Content-Type')) {
      resolvedHeaders = resolvedHeaders.set('Content-Type', 'application/json');
    }

    if (isAuthNeeded) {
      const token = this.tokens.accessToken;
      if (token) {
        resolvedHeaders = resolvedHeaders.set('Authorization', `Bearer ${token}`);
      }
    }

    return {
      ...rest,
      headers: resolvedHeaders
    };
  }

  private buildUrl(endpoint: string): string {
    if (/^(https?:)?\/\//i.test(endpoint)) {
      return endpoint;
    }

    const sanitizedEndpoint = endpoint.replace(/^\/+/, '');
    return `${this.baseUrl}/${sanitizedEndpoint}`;
  }
}
